import { useQueryClient } from '@tanstack/react-query'
import { router } from 'expo-router'
import { useState, type ReactElement } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { Message } from '@/src/elements/message'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { ABUSE_TYPE } from '@/src/features/abuse/models/Abuse.enums'
import { ReportAbuseModal } from '@/src/features/abuse/ui/ReportAbuseModal/ReportAbuseModal'
import type { TGetIntentionsPayload } from '@/src/features/intention/api/types/getIntentionsApi.types'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import type { TTypeIntention } from '@/src/features/intention/models/Intention.types'
import { getIntentionsQueryOptions } from '@/src/features/intention/services/useIntentionsQuery'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { IntentionCard, type TIntentionCardProps } from '../../../components/IntentionCard'

type TIntentionProps<Type extends INTENTION_TYPE> = Pick<
  TIntentionCardProps<Type>,
  'isLoading' | 'intention' | 'type'
> & {
  intentionsQueryPayload: TGetIntentionsPayload
  intention?: TTypeIntention
  trackingSource: TRACKING_SOURCE
}

export const Intention = <Type extends INTENTION_TYPE>({
  isLoading,
  intention,
  intentionsQueryPayload,
  type,
  trackingSource,
}: TIntentionProps<Type>): ReactElement => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const hasPrayed = intention?.hasPrayed

  const handlePressPray = () => {
    if (intention)
      router.navigate(
        routes.intentionsPray({
          id: intention.id,
          shareToken: 'shareToken' in intention ? intention.shareToken : undefined,
          type,
          source: trackingSource,
          theme: intentionsQueryPayload.landingPageSlug,
        }),
      )
  }

  const getHasPrayedColor = () => (hasPrayed ? 'success' : 'default')

  const buttonTestId = intention ? TEST_ID.INTENTIONS.CARD.PRAY_BUTTON(type, intention.id) : undefined

  /** Once prayed: minus 1 to not count the current user's prayer */
  const otherPrayersCount = intention ? Math.max(intention.prayersCount - 1, 0) : 0
  const totalPrayersCount = intention ? intention.prayersCount : 0

  return (
    <>
      <IntentionCard
        intention={intention}
        isLoading={isLoading}
        type={type}
        hideBody={hasPrayed}
        color={getHasPrayedColor()}
        options={
          !hasPrayed && type === INTENTION_TYPE.INTENTION
            ? [
                {
                  onPress: () => setIsModalVisible(true),
                  children: t('app.report-abuse.intention'),
                  startContent: <Icon name={ICON.ALERT_TRIANGLE} size="sm" />,
                  testID: TEST_ID.INTENTION.OPTIONS.REPORT_ABUSE,
                },
              ]
            : undefined
        }
        showUser
      >
        {!!isLoading && <Spacer y={6} />}
        {!isLoading && !!hasPrayed && (
          <Message
            iconName={ICON.CHECK_CIRCLE_BROKEN}
            colors="success"
            testID={TEST_ID.INTENTIONS.CARD.PRAY_SUCCESS(type, intention.id)}
          >
            {type !== INTENTION_TYPE.BIRTHDAY && t('intention.prayed.already-with', { count: otherPrayersCount })}
            {type === INTENTION_TYPE.BIRTHDAY &&
              t('intention.prayed.birthday', { count: otherPrayersCount, userName: intention.user?.name })}
          </Message>
        )}
        {!isLoading && !hasPrayed && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon size="sm" className="text-default-text-contrast-low" name={ICON.FIRE} />
            <Typography colors="default" contrast="low" size="sm" className="grow pt-1">
              {t('word.prayers-count', { count: totalPrayersCount })}
            </Typography>
            <Button
              onPress={handlePressPray}
              size="sm"
              colors="primary"
              variant="solid"
              startIcon={ICON.FIRE}
              testID={buttonTestId}
            >
              {t('pray.i-pray.short')}
            </Button>
          </View>
        )}
      </IntentionCard>
      {intention && intention.type === INTENTION_TYPE.INTENTION && (
        <ReportAbuseModal
          showModal={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onDone={() => {
            addToast({
              message: t('app.report-abuse.intention.success'),
              colors: 'success',
              iconName: ICON.MESSAGE_INFO,
            })
            queryClient.setQueryData(getIntentionsQueryOptions(intentionsQueryPayload, null).queryKey, (intentionIds) =>
              intentionIds?.filter((id) => id !== intention.id),
            )
          }}
          objectId={intention.id}
          objectType={ABUSE_TYPE.INTENTION}
          text={intention.content}
        />
      )}
    </>
  )
}
